import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import AppSidebar from "@/components/layout/AppSidebar";
import AppSidebarRedesign from "@/components/layout/AppSidebarRedesign";
import MobileSidebar from "@/components/layout/MobileSidebar";
import MobileSidebarRedesign from "@/components/layout/MobileSidebarRedesign";
import { ContentHeader } from "@/components/layout/ContentHeader";
import { SectionTabs } from "@/components/layout/SectionTabs";
import TrialBanner from "@/components/payment/TrialBanner";
import BackToTop from "@/components/layout/BackToTop";
import PhoneTabBar from "@/components/layout/PhoneTabBar";
import PhoneTabBarRedesign from "@/components/layout/PhoneTabBarRedesign";
import { useRedesignNav } from "@/lib/redesignNav";
import { WelcomeGuideHost } from "@/components/dashboard/WelcomeGuideHost";
import { SubscriptionGate } from "@/components/payment/SubscriptionGate";
import { PageQueryErrorNotice } from "@/components/QueryErrorNotice";
import { PageGuidanceProvider } from "@/contexts/PageGuidanceContext";

const SIDEBAR_STORAGE_KEY = "fomo-fi-sidebar-collapsed";
// In the sidebars' own units: expanded is `w-64` (16rem), collapsed `w-[72px]`. The offset
// was 256px for both, so at 200% text the 512px sidebar covered the left of every page
// (F1 render pass, 2026-10-19). Exported for the test that pins the two together.
export const SIDEBAR_COLLAPSED_WIDTH = "72px";
export const SIDEBAR_EXPANDED_WIDTH = "16rem";

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}

/**
 * The signed-in shell.
 *
 * `children` replaces the page area — the shared failure notice, the trial
 * banner and the subscription gate that owns the route's `<Outlet/>` — and
 * nothing else. It exists so `OnboardingGate` can report that it could not read
 * the account WITHOUT swapping the layout for a different component: React
 * reconciles by element type, so a gate that returns `AppLayout` in one state
 * and a bare `div` in another unmounts and remounts the whole shell, and every
 * query inside it, on each transition. That is what produced twenty-five
 * layouts on one load of /payment in the QA marathon's second baseline
 * (BEN-141 QF8). Same component, different content: one mount.
 */
export default function AppLayout({ children }: { children?: React.ReactNode } = {}) {
  const { collapsed, toggle } = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const redesign = useRedesignNav();
  // The forecast page is dark-canonical: give the content column the forecast
  // background (no light bar/corner behind the header) and drop the rounded
  // top-left corner so it reads as one continuous dark surface.
  const isForecast = useLocation().pathname === "/forecast";

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  // FP-181: tells the floating feedback stack and Back to top (outside this tree's
  // DOM for the widget) that a sidebar is here and how wide, so from lg they dock in
  // its bottom-left instead of over the content's right-aligned figures. Removed on
  // unmount, so a page without the sidebar keeps them in the corner.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.appSidebar = collapsed ? "collapsed" : "expanded";
    root.style.setProperty("--app-sidebar-w", sidebarWidth);
    return () => {
      delete root.dataset.appSidebar;
      root.style.removeProperty("--app-sidebar-w");
    };
  }, [sidebarWidth, collapsed]);

  return (
    <PageGuidanceProvider>
      <div className="min-h-screen bg-background">
        {/* Doc 3: sign-up runs "before the nav appears". A sign-up step in the app shell
            (the payment step, SignupShell) hides everything marked data-app-chrome;
            see :root[data-signup-shell] in index.css. */}
        <div data-app-chrome="">
          {/* RD-08: the redesign's nav only with the server switch on; otherwise today's. */}
          {redesign ? (
            <AppSidebarRedesign collapsed={collapsed} onToggle={toggle} />
          ) : (
            <AppSidebar collapsed={collapsed} onToggle={toggle} />
          )}
          {redesign ? (
            <MobileSidebarRedesign open={mobileOpen} onOpenChange={setMobileOpen} />
          ) : (
            <MobileSidebar open={mobileOpen} onOpenChange={setMobileOpen} />
          )}
        </div>

        {/* Mobile top bar. The safe-area insets are 0 on desktop browsers, so these
            calc()/max() forms render exactly as h-14 / px-4 there; on an iPhone they
            keep the bar clear of the status area and, in landscape, the notch. */}
        <header
          data-app-chrome=""
          className="fixed inset-x-0 top-0 z-30 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center justify-between border-b border-border/60 bg-background/80 pt-[env(safe-area-inset-top)] pr-[max(1rem,env(safe-area-inset-right))] pl-[max(1rem,env(safe-area-inset-left))] backdrop-blur-xl lg:hidden"
        >
          <FomoFiLogo size="md" />
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
            // Phase 3: below 768px the tab bar's More replaces the drawer.
            className="hidden rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground md:block"
          >
            <Menu className="h-5 w-5" />
          </button>
        </header>

        {/* Main content — sidebar offset applied on lg+ only via CSS var */}
        <main
          className="app-main pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 transition-[padding-left] duration-300"
          style={{ "--sidebar-w": sidebarWidth } as React.CSSProperties}
        >
          <div
            className={
              isForecast
                ? "min-h-screen bg-[var(--forecast-bg)]"
                : "min-h-screen bg-muted/40 lg:rounded-tl-3xl"
            }
          >
            <div data-app-content="" className="px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
              <div data-app-chrome="">
                <ContentHeader />
                {redesign && <SectionTabs />}
              </div>
              {children ?? (
                <>
                  {/* One shared failure surface for every signed-in route:
                      whatever the page asked the API for and did not get, said
                      once, in the content column, with a retry (BEN-141 QF4).
                      Pages that own a more specific error state keep it; this
                      is the floor. */}
                  <PageQueryErrorNotice />
                  <TrialBanner />
                  <SubscriptionGate />
                </>
              )}
            </div>
          </div>
        </main>

        {/* Every app route scrolls the window, so one control covers all of them. */}
        <BackToTop />

        {/* Phase 3: phone-width tabs (below 768px only). */}
        <div data-app-chrome="">{redesign ? <PhoneTabBarRedesign /> : <PhoneTabBar />}</div>

        {/* Once per user, on whichever authed route they land on first — see the host. */}
        {/* H9: no teaching on Home until RD-35's banner; the switch-off app keeps its box. */}
        {!redesign && <WelcomeGuideHost />}
      </div>
    </PageGuidanceProvider>
  );
}
